import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth, getToken } from '../App';
import AuthModal from './AuthModal';
import { fmtNum } from '../utils/numerology';

function formatDate(iso) {
  try { return new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
}

function StatCard({ label, value, sub, color }) {
  return (
    <div style={{
      background: 'white', border: '1px solid var(--border)', borderRadius: 16,
      padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 4,
    }}>
      <div style={{ fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--muted)' }}>{label}</div>
      <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 40, fontWeight: 300, color: color || 'var(--deep)', lineHeight: 1 }}>{value ?? '—'}</div>
      {sub && <div style={{ fontSize: 12, color: 'var(--muted)' }}>{sub}</div>}
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [emails, setEmails] = useState([]);
  const [readings, setReadings] = useState([]);
  const [tab, setTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [showAuth, setShowAuth] = useState(false);
  const [authError, setAuthError] = useState('');
  const [feedback, setFeedback] = useState([]);
  const [accuracy, setAccuracy] = useState(null);
  const [users, setUsers] = useState([]);

  const load = async () => {
    const token = getToken();
    if (!token) { setLoading(false); return; }
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [s, e, r, f, a, u] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/emails`, { headers }),
        axios.get(`${API}/admin/readings`, { headers }),
        axios.get(`${API}/admin/feedback`, { headers }),
        axios.get(`${API}/admin/reading-feedback`, { headers }),
        axios.get(`${API}/admin/users`, { headers }),
      ]);
      setStats(s.data);
      setEmails(e.data);
      setReadings(r.data);
      setFeedback(f.data);
      setAccuracy(a.data);
      setUsers(u.data);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        setAuthError(err.response?.status === 403 ? 'Admin access required' : 'Please sign in');
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [user]);

  const handleExportEmails = () => {
    const csv = ['Name,Email,Source,Captured At', ...emails.map(e => `"${e.name || ''}","${e.email}","${e.source || 'general'}","${e.captured_at}"`)].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'lbn-emails.csv'; a.click();
  };

  // Delete helpers — gated by window.confirm so Jemma can't accidentally
  // nuke a real user's data by misclick. Updates local state optimistically
  // on success so the UI feels snappy.
  const authHeaders = () => ({ Authorization: `Bearer ${getToken()}` });

  const deleteEmail = async (email) => {
    if (!window.confirm(`Delete ${email}?\n\nThis removes them from email_captures.`)) return;
    try {
      await axios.delete(`${API}/admin/emails/${encodeURIComponent(email)}`, { headers: authHeaders() });
      setEmails(prev => prev.filter(x => x.email !== email));
    } catch (err) {
      alert(err.response?.data?.detail || 'Could not delete email');
    }
  };

  const deleteReading = async (sessionId) => {
    if (!window.confirm('Delete this reading?\n\nAlso removes any accuracy feedback for it.')) return;
    try {
      await axios.delete(`${API}/admin/readings/${encodeURIComponent(sessionId)}`, { headers: authHeaders() });
      setReadings(prev => prev.filter(x => x.session_id !== sessionId));
    } catch (err) {
      alert(err.response?.data?.detail || 'Could not delete reading');
    }
  };

  const deleteUser = async (u) => {
    const label = `${u.name || ''} <${u.email}>`;
    if (!window.confirm(`DELETE user ${label}?\n\nThis permanently removes their account, ALL their readings, family members, accuracy feedback, and email captures. This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${u._id}`, { headers: authHeaders() });
      setUsers(prev => prev.filter(x => x._id !== u._id));
      // refresh dependent lists
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Could not delete user');
    }
  };

  const deleteFeedback = async (id) => {
    if (!window.confirm('Delete this feedback note?')) return;
    try {
      await axios.delete(`${API}/admin/feedback/${id}`, { headers: authHeaders() });
      setFeedback(prev => prev.filter(x => x._id !== id));
    } catch (err) {
      alert(err.response?.data?.detail || 'Could not delete feedback');
    }
  };

  if (!user && !loading) {
    return (
      <div style={{ maxWidth: 480, margin: '80px auto', padding: '0 24px', textAlign: 'center' }}>
        <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 32, fontWeight: 300, marginBottom: 8 }}>Admin Dashboard</div>
        <p style={{ color: 'var(--muted)', marginBottom: 24 }}>Sign in with your admin account to continue.</p>
        {authError && <div style={{ background: 'rgba(220,38,38,0.08)', borderRadius: 10, padding: '10px 14px', fontSize: 13, color: '#dc2626', marginBottom: 16 }}>{authError}</div>}
        <button className="tool-btn" onClick={() => setShowAuth(true)} data-testid="admin-signin-btn">Sign in</button>
        {showAuth && <AuthModal onClose={() => setShowAuth(false)} onSuccess={() => { setShowAuth(false); load(); }} />}
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}>
        <div className="loading-dots"><div className="dot" /><div className="dot" /><div className="dot" /></div>
      </div>
    );
  }

  if (authError) {
    return (
      <div style={{ maxWidth: 480, margin: '80px auto', padding: '0 24px', textAlign: 'center' }}>
        <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 28, fontWeight: 300, marginBottom: 8 }}>{authError}</div>
        <p style={{ color: 'var(--muted)', marginBottom: 24 }}>Your account doesn't have admin access.</p>
        <button className="tool-btn" onClick={() => navigate('/')} >Go home</button>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '40px 32px' }} data-testid="admin-dashboard">
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 32, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 36, fontWeight: 300 }}>Admin Dashboard</div>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 2 }}>Little Blue Numbers · Launch tracking</div>
        </div>
        <button className="nav-btn" onClick={() => navigate('/')}>Back to site</button>
      </div>

      {/* Tabs */}
      <div className="auth-tabs" style={{ marginBottom: 32, borderBottom: '1px solid var(--border)' }}>
        {['overview', 'accuracy', 'emails', 'feedback', 'readings', 'users'].map(t => (
          <button key={t} className={`auth-tab ${tab === t ? 'active' : ''}`} onClick={() => setTab(t)} data-testid={`admin-tab-${t}`} style={{ textTransform: 'capitalize' }}>{t}</button>
        ))}
      </div>

      {/* OVERVIEW */}
      {tab === 'overview' && stats && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 32 }}>
            <StatCard label="Total readings" value={stats.total_readings} sub={`${stats.saved_readings} saved`} color="var(--blue-dark)" />
            <StatCard
              label="Resonance rate"
              value={accuracy?.total ? `${accuracy.resonance_rate}%` : '—'}
              sub={accuracy?.total ? `${accuracy.total} responses` : 'Awaiting first vote'}
              color="var(--mauve-dark)"
            />
            <StatCard label="Waitlist signups" value={stats.waitlist_count ?? 0} sub="From reading page" color="var(--mauve-dark)" />
            <StatCard label="All email captures" value={stats.total_emails} sub="Includes waitlist" color="var(--mauve)" />
            <StatCard label="User accounts" value={stats.total_users} sub="Registered" color="var(--deep)" />
            <StatCard label="Save rate" value={stats.total_readings > 0 ? `${Math.round(stats.saved_readings / stats.total_readings * 100)}%` : '—'} sub="Of readings saved" color="var(--blue)" />
          </div>

          {stats.readings_by_stage?.length > 0 && (
            <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 16, padding: 24, marginBottom: 16 }}>
              <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, fontWeight: 300, marginBottom: 16 }}>Readings by life stage</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {stats.readings_by_stage.map(s => (
                  <div key={s._id} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ width: 180, fontSize: 13, color: 'var(--deep)' }}>{s._id || 'Unknown'}</div>
                    <div style={{ flex: 1, background: 'var(--cream2)', borderRadius: 100, height: 8, overflow: 'hidden' }}>
                      <div style={{
                        height: '100%', borderRadius: 100,
                        width: `${Math.round(s.count / stats.total_readings * 100)}%`,
                        background: 'linear-gradient(90deg, var(--blue-dark), var(--mauve-dark))',
                      }} />
                    </div>
                    <div style={{ width: 28, fontSize: 13, color: 'var(--muted)', textAlign: 'right' }}>{s.count}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {stats.readings_by_mode?.length > 0 && (
            <div style={{ display: 'flex', gap: 12 }}>
              {stats.readings_by_mode.map(m => (
                <div key={m._id} style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 12, padding: '14px 20px', flex: 1 }}>
                  <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 4, textTransform: 'capitalize' }}>{m._id} readings</div>
                  <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 28, fontWeight: 300 }}>{m.count}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ACCURACY */}
      {tab === 'accuracy' && (
        <div data-testid="admin-accuracy">
          {(() => {
            const a = accuracy || { total: 0, accurate: 0, mostly: 0, not_really: 0, resonance_rate: 0, notes: [] };
            const pct = (n) => a.total > 0 ? Math.round((n / a.total) * 100) : 0;
            const bar = (label, value, color) => (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                <div style={{ width: 140, fontSize: 13, color: 'var(--deep)' }}>{label}</div>
                <div style={{ flex: 1, background: 'var(--cream2)', borderRadius: 100, height: 10, overflow: 'hidden' }}>
                  <div style={{ height: '100%', borderRadius: 100, width: `${pct(value)}%`, background: color }} />
                </div>
                <div style={{ width: 70, fontSize: 13, color: 'var(--muted)', textAlign: 'right' }}>{value} ({pct(value)}%)</div>
              </div>
            );
            return (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 28 }}>
                  <StatCard label="Resonance rate" value={a.total ? `${a.resonance_rate}%` : '—'} sub="Said accurate or mostly" color="var(--mauve-dark)" />
                  <StatCard label="Total responses" value={a.total} sub="On post-reading prompt" color="var(--blue-dark)" />
                  <StatCard label="With a note" value={a.notes.length} sub="Free-text quotes" color="var(--blue)" />
                </div>

                <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 16, padding: 24, marginBottom: 24 }}>
                  <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, fontWeight: 300, marginBottom: 16 }}>Breakdown</div>
                  {a.total === 0 ? (
                    <div style={{ color: 'var(--muted)', fontSize: 14, padding: '8px 0' }}>No responses yet — the prompt appears below every reading once Claude finishes writing it.</div>
                  ) : (
                    <>
                      {bar("Yes, that's them", a.accurate, 'linear-gradient(90deg, var(--blue-dark), var(--mauve-dark))')}
                      {bar('Mostly', a.mostly, 'var(--mauve)')}
                      {bar('Not really', a.not_really, 'var(--muted)')}
                    </>
                  )}
                </div>

                <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, fontWeight: 300, marginBottom: 12 }}>
                  Recent quotes <span style={{ fontSize: 13, color: 'var(--muted)', fontFamily: "'Figtree', sans-serif" }}>· these are testimonial gold</span>
                </div>
                {a.notes.length === 0 ? (
                  <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 16, padding: 24, color: 'var(--muted)', fontSize: 14, textAlign: 'center' }}>
                    No written notes yet. They'll appear here as readers submit them.
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {a.notes.map((n, i) => {
                      const pillBg = n.accuracy === 'accurate' ? 'var(--mauve-pale)' : n.accuracy === 'mostly' ? 'var(--blue-pale)' : 'var(--cream2)';
                      const pillColor = n.accuracy === 'accurate' ? 'var(--mauve-dark)' : n.accuracy === 'mostly' ? 'var(--blue-dark)' : 'var(--muted)';
                      const pillLabel = n.accuracy === 'accurate' ? "Yes, that's them" : n.accuracy === 'mostly' ? 'Mostly' : 'Not really';
                      return (
                        <div key={i} style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 16, padding: 20 }} data-testid={`accuracy-note-${i}`}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
                            <span style={{ background: pillBg, color: pillColor, borderRadius: 100, padding: '3px 10px', fontSize: 11, fontWeight: 500 }}>{pillLabel}</span>
                            {n.name && <span style={{ fontSize: 13, color: 'var(--deep)' }}>{n.name}</span>}
                            {n.stage_label && <span style={{ fontSize: 12, color: 'var(--muted)' }}>{n.stage_label}</span>}
                            {n.mode && <span style={{ fontSize: 11, color: 'var(--muted)', textTransform: 'capitalize' }}>· {n.mode}</span>}
                            <span style={{ fontSize: 11, color: 'var(--muted)', marginLeft: 'auto' }}>{formatDate(n.updated_at || n.created_at)}</span>
                          </div>
                          <p style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic', fontSize: 17, color: 'var(--deep)', lineHeight: 1.7, margin: 0 }}>
                            &ldquo;{n.note}&rdquo;
                          </p>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            );
          })()}
        </div>
      )}

      {/* EMAILS */}
      {tab === 'emails' && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 24, fontWeight: 300 }}>
              {emails.length} email{emails.length !== 1 ? 's' : ''} captured
            </div>
            {emails.length > 0 && (
              <button className="nav-btn" onClick={handleExportEmails} data-testid="export-emails-btn">Export CSV</button>
            )}
          </div>
          {emails.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--muted)' }}>No emails captured yet</div>
          ) : (
            <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 16, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--cream2)' }}>
                    <th style={{ padding: '12px 20px', textAlign: 'left', fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--muted)', fontWeight: 400 }}>Email</th>
                    <th style={{ padding: '12px 20px', textAlign: 'left', fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--muted)', fontWeight: 400 }}>Name</th>
                    <th style={{ padding: '12px 20px', textAlign: 'left', fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--muted)', fontWeight: 400 }}>Source</th>
                    <th style={{ padding: '12px 20px', textAlign: 'left', fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--muted)', fontWeight: 400 }}>Captured</th>
                    <th style={{ padding: '12px 20px', textAlign: 'right', fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--muted)', fontWeight: 400 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {emails.map((e, i) => {
                    const src = e.source || 'general';
                    const isWaitlist = src === 'waitlist_after_reading';
                    const isSignup = src === 'user_signup';
                    let pillBg = 'var(--cream2)';
                    let pillColor = 'var(--muted)';
                    let pillLabel = src;
                    if (isWaitlist) { pillBg = 'var(--mauve-pale)'; pillColor = 'var(--mauve-dark)'; pillLabel = 'Waitlist'; }
                    else if (isSignup) { pillBg = 'var(--blue-pale)'; pillColor = 'var(--blue-dark)'; pillLabel = 'Sign-up'; }
                    return (
                      <tr key={i} style={{ borderBottom: '1px solid var(--border)' }} data-testid={`email-row-${i}`}>
                        <td style={{ padding: '12px 20px', color: 'var(--deep)' }}>{e.email}</td>
                        <td style={{ padding: '12px 20px', color: 'var(--muted)' }}>{e.name || '—'}</td>
                        <td style={{ padding: '12px 20px' }}>
                          <span style={{
                            background: pillBg,
                            color: pillColor,
                            borderRadius: 100, padding: '3px 10px', fontSize: 11, fontWeight: 500,
                          }} data-testid={`email-source-${i}`}>
                            {pillLabel}
                          </span>
                        </td>
                        <td style={{ padding: '12px 20px', color: 'var(--muted)', fontSize: 12 }}>{formatDate(e.captured_at)}</td>
                        <td style={{ padding: '8px 20px', textAlign: 'right' }}>
                          <button
                            className="admin-row-del-btn"
                            onClick={() => deleteEmail(e.email)}
                            data-testid={`delete-email-${i}`}
                            aria-label={`Delete ${e.email}`}
                            title="Delete"
                          >
                            ×
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* FEEDBACK */}
      {tab === 'feedback' && (
        <div>
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 24, fontWeight: 300, marginBottom: 16 }}>
            {feedback.length} piece{feedback.length !== 1 ? 's' : ''} of feedback
          </div>
          {feedback.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--muted)' }}>No feedback yet</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {feedback.map((f, i) => (
                <div key={i} style={{ position: 'relative', background: 'white', border: '1px solid var(--border)', borderRadius: 16, padding: 20 }} data-testid={`feedback-item-${i}`}>
                  <button
                    className="admin-row-del-btn"
                    onClick={() => deleteFeedback(f._id)}
                    data-testid={`delete-feedback-${i}`}
                    aria-label="Delete feedback"
                    title="Delete"
                    style={{ position: 'absolute', top: 10, right: 10 }}
                  >
                    ×
                  </button>
                  <p style={{ fontSize: 15, color: 'var(--deep)', lineHeight: 1.75, marginBottom: 10, paddingRight: 30 }}>{f.message}</p>
                  <div style={{ display: 'flex', gap: 16, fontSize: 12, color: 'var(--muted)' }}>
                    {f.name && <span>{f.name}</span>}
                    {f.email && <span>{f.email}</span>}
                    <span>{formatDate(f.created_at)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* READINGS */}
      {tab === 'readings' && (
        <div>
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 24, fontWeight: 300, marginBottom: 16 }}>
            {readings.length} recent reading{readings.length !== 1 ? 's' : ''}
          </div>
          {readings.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--muted)' }}>No readings yet</div>
          ) : (
            <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 16, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--cream2)' }}>
                    {['Name', 'Life Stage', 'Mode', 'Life Path', 'Saved', 'Date', ''].map(h => (
                      <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--muted)', fontWeight: 400 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {readings.map((r, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid var(--border)' }} data-testid={`reading-row-${i}`}>
                      <td style={{ padding: '12px 16px', fontWeight: 500 }}>{r.name}</td>
                      <td style={{ padding: '12px 16px', color: 'var(--muted)', fontSize: 13 }}>{r.stage_label}</td>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{ background: r.mode === 'child' ? 'var(--mauve-pale)' : 'var(--blue-pale)', color: r.mode === 'child' ? 'var(--mauve-dark)' : 'var(--blue-dark)', borderRadius: 100, padding: '3px 10px', fontSize: 11 }}>{r.mode}</span>
                      </td>
                      <td style={{ padding: '12px 16px', fontFamily: "'Cormorant Garamond', serif", fontSize: 20 }}>{fmtNum(r.numbers?.life_path)}</td>
                      <td style={{ padding: '12px 16px', color: r.user_id ? 'var(--blue-dark)' : 'var(--muted)', fontSize: 12 }}>{r.user_id ? '✓ saved' : 'no'}</td>
                      <td style={{ padding: '12px 16px', color: 'var(--muted)', fontSize: 12 }}>{formatDate(r.created_at)}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                        <button
                          className="admin-row-del-btn"
                          onClick={() => deleteReading(r.session_id)}
                          data-testid={`delete-reading-${i}`}
                          aria-label="Delete reading"
                          title="Delete reading"
                        >
                          ×
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* USERS */}
      {tab === 'users' && (
        <div data-testid="admin-users-tab">
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 24, fontWeight: 300, marginBottom: 16 }}>
            {users.length} user{users.length !== 1 ? 's' : ''}
            <span style={{ fontSize: 13, color: 'var(--muted)', marginLeft: 10, fontFamily: "'Manrope', sans-serif" }}>
              · Deleting a user also removes their readings, family, and email captures
            </span>
          </div>
          {users.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--muted)' }}>No users yet</div>
          ) : (
            <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 16, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--cream2)' }}>
                    {['Name', 'Email', 'Type', 'Role', 'Founding', 'Joined', ''].map(h => (
                      <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--muted)', fontWeight: 400 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {users.map((u, i) => {
                    const isAdmin = u.role === 'admin';
                    return (
                      <tr key={u._id} style={{ borderBottom: '1px solid var(--border)' }} data-testid={`user-row-${i}`}>
                        <td style={{ padding: '12px 16px', fontWeight: 500 }}>{u.name || '—'}</td>
                        <td style={{ padding: '12px 16px', color: 'var(--muted)', fontSize: 13 }}>{u.email}</td>
                        <td style={{ padding: '12px 16px' }}>
                          <span style={{ fontSize: 11, color: 'var(--muted)', background: 'var(--cream2)', padding: '2px 10px', borderRadius: 100, letterSpacing: '0.04em' }}>
                            {u.account_type || 'full'}
                          </span>
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          {isAdmin
                            ? <span style={{ fontSize: 11, color: 'var(--blue-dark)', fontWeight: 500 }}>admin</span>
                            : <span style={{ fontSize: 11, color: 'var(--muted)' }}>user</span>}
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: 12 }}>
                          {u.founding_member
                            ? <span style={{ color: 'var(--mauve-dark)' }}>✦ Yes</span>
                            : <span style={{ color: 'var(--muted)' }}>—</span>}
                        </td>
                        <td style={{ padding: '12px 16px', color: 'var(--muted)', fontSize: 12 }}>{formatDate(u.created_at)}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                          <button
                            className="admin-row-del-btn"
                            onClick={() => deleteUser(u)}
                            disabled={isAdmin}
                            data-testid={`delete-user-${i}`}
                            aria-label={`Delete ${u.email}`}
                            title={isAdmin ? "Can't delete admin" : 'Delete user'}
                          >
                            ×
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

    </div>
  );
}
